import React, { useState, useEffect, useMemo } from 'react';
import { Trip, StudentProfile, PaymentMethod, University, PaymentAttempt, PaymentStatus } from '../../../types';
import { adminBookingService, mapPaymentMethodToVault } from '../../../services/adminBookingService';
import { treasuryService } from '../../../services/treasuryService';
import { paymentApi } from '../../../services/paymentApi';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config/firebase';
import {
  User,
  Search,
  Calendar,
  Clock,
  CheckCircle2,
  X,
  CreditCard,
  MessageSquare,
  Armchair,
  GraduationCap,
  Phone,
  AlertCircle,
  ExternalLink,
  ChevronRight,
  ChevronLeft,
  Users,
  RefreshCw,
  Layers,
  Plus,
  Trash2,
  CheckSquare,
} from 'lucide-react';

export interface QueuedBookingItem {
  id: string;
  trip: Trip;
  date: string;
  direction: 'OUTBOUND' | 'RETURN';
  departureTime: string;
  universityName: string;
  price: number;
  seatNumber: number;
}

export interface AdminDirectBookingModalProps {
  students: StudentProfile[];
  trips: Trip[];
  universities: University[];
  defaultDate?: string;
  defaultStudentUid?: string;
  initialStudent?: StudentProfile | null;
  initialTripBookingMode?: 'ROUND_TRIP' | 'ONE_WAY_OUTBOUND' | 'ONE_WAY_RETURN';
  initialSelectedDates?: string[];
  initialQueue?: QueuedBookingItem[];
  initialSelectedTripId?: string;
  initialSelectedSeatNumber?: number | null;
  initialPaymentMethod?: PaymentMethod;
  initialIsPaid?: boolean;
  initialAdminNote?: string;
  modalTitle?: string;
  modalSubtitle?: string;
  adminEmail?: string;
  editingAttempt?: PaymentAttempt | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];

const ARABIC_WEEKDAYS = ['السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'];

const getDayOfWeekName = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '';
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  const jsDay = d.getDay();
  const mapping: Record<number, string> = {
    6: 'السبت',
    0: 'الأحد',
    1: 'الاثنين',
    2: 'الثلاثاء',
    3: 'الأربعاء',
    4: 'الخميس',
    5: 'الجمعة',
  };
  return mapping[jsDay] || '';
};

const formatLocalDate = (year: number, monthZeroIndexed: number, day: number): string => {
  const m = String(monthZeroIndexed + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
};

const getTodayIso = (): string => {
  const now = new Date();
  return formatLocalDate(now.getFullYear(), now.getMonth(), now.getDate());
};

export const AdminDirectBookingModal: React.FC<AdminDirectBookingModalProps> = ({
  students,
  trips,
  universities,
  defaultDate,
  defaultStudentUid,
  initialStudent,
  initialTripBookingMode,
  initialSelectedDates,
  initialQueue,
  initialSelectedTripId,
  initialSelectedSeatNumber,
  initialPaymentMethod,
  initialIsPaid,
  initialAdminNote,
  modalTitle,
  modalSubtitle,
  adminEmail,
  editingAttempt,
  onClose,
  onSuccess,
}) => {
  // Helper to cleanly purge/release previous attempt bookings and seats when in edit mode
  const cleanupPreviousAttempt = async () => {
    if (!editingAttempt) return;
    try {
      await paymentApi.deletePaymentAttempt({
        paymentAttemptId: editingAttempt.id,
        bookingId: editingAttempt.bookingId,
        bookingGroupId: editingAttempt.bookingGroupId,
        bookingCode: editingAttempt.bookingCode,
        bookingGroupCode: editingAttempt.bookingGroupCode,
        tripId: editingAttempt.tripId,
        seatNumber: editingAttempt.seatNumber,
        permanent: true,
      });
    } catch (e) {
      console.warn('Could not clean up previous attempt before edit save:', e);
    }
  };

  // 1. Student Selection State
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentDirectory, setShowStudentDirectory] = useState(false);
  const [directorySearch, setDirectorySearch] = useState('');

  const [selectedStudent, setSelectedStudent] = useState<StudentProfile | null>(() => {
    if (initialStudent) return initialStudent;
    if (defaultStudentUid) {
      return students.find((s) => s.uid === defaultStudentUid || s.phoneNumber === defaultStudentUid) || null;
    }
    return null;
  });

  // 2. Booking Journey Mode & Line State
  // 'ROUND_TRIP' (Same Day Outbound + Return), 'ONE_WAY_OUTBOUND', 'ONE_WAY_RETURN'
  const [tripBookingMode, setTripBookingMode] = useState<'ROUND_TRIP' | 'ONE_WAY_OUTBOUND' | 'ONE_WAY_RETURN'>(
    initialTripBookingMode || 'ROUND_TRIP'
  );
  const [selectedUniId, setSelectedUniId] = useState<string>(() => {
    if (initialStudent?.universityId) return initialStudent.universityId;
    return 'ALL';
  });
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    if (defaultDate) return defaultDate;
    if (initialSelectedDates && initialSelectedDates.length > 0) return initialSelectedDates[0];
    return getTodayIso();
  });

  // Multi-Day and Multi-Time (Schedule Queue) State
  const [isMultiMode, setIsMultiMode] = useState<boolean>(() => {
    if (initialQueue && initialQueue.length > 0) return true;
    if (initialSelectedDates && initialSelectedDates.length > 1) return true;
    return false;
  });
  const [selectedDates, setSelectedDates] = useState<string[]>(() => {
    if (initialSelectedDates && initialSelectedDates.length > 0) return initialSelectedDates;
    if (defaultDate) return [defaultDate];
    return [getTodayIso()];
  });
  const [bookingQueue, setBookingQueue] = useState<QueuedBookingItem[]>(() => {
    if (initialQueue && initialQueue.length > 0) return initialQueue;
    return [];
  });

  // Calendar Month Navigation
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const targetDate = defaultDate || (initialSelectedDates && initialSelectedDates[0]);
    if (targetDate) {
      const parts = targetDate.split('-');
      if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
      }
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // 3. Outbound Trip & Seat State
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(() => {
    if (initialSelectedTripId && trips.length > 0) {
      return trips.find((t) => t.id === initialSelectedTripId) || null;
    }
    return null;
  });
  const [selectedSeatNumber, setSelectedSeatNumber] = useState<number | null>(() => {
    if (typeof initialSelectedSeatNumber === 'number') return initialSelectedSeatNumber;
    return null;
  });
  const [tripManifestLoading, setTripManifestLoading] = useState(false);
  const [occupiedSeats, setOccupiedSeats] = useState<number[]>([]);

  useEffect(() => {
    if (initialSelectedTripId && trips.length > 0 && !selectedTrip) {
      const match = trips.find((t) => t.id === initialSelectedTripId);
      if (match) setSelectedTrip(match);
    }
  }, [initialSelectedTripId, trips, selectedTrip]);

  // 4. Return Trip & Seat State (for ROUND_TRIP)
  const [selectedReturnTrip, setSelectedReturnTrip] = useState<Trip | null>(null);
  const [selectedReturnSeatNumber, setSelectedReturnSeatNumber] = useState<number | null>(null);
  const [returnTripManifestLoading, setReturnTripManifestLoading] = useState(false);
  const [returnOccupiedSeats, setReturnOccupiedSeats] = useState<number[]>([]);

  // 5. Payment & Submit State
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(initialPaymentMethod || 'CASH');
  const [isPaid, setIsPaid] = useState(initialIsPaid !== undefined ? initialIsPaid : true);
  const [adminNote, setAdminNote] = useState(initialAdminNote || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successBookingResult, setSuccessBookingResult] = useState<{
    isMulti?: boolean;
    totalTripsCount?: number;
    totalPrice?: number;
    multiBookings?: Array<{
      tripDate: string;
      direction: string;
      departureTime: string;
      universityName: string;
      seatNumber: number;
      bookingCode: string;
      ticketId: string;
      price: number;
    }>;
    isRoundTrip: boolean;
    bookingCode: string;
    ticketId: string;
    studentPhone?: string;
    whatsAppUrl?: string;
    returnBookingCode?: string;
    returnTicketId?: string;
    returnSeatNumber?: number | null;
  } | null>(null);

  // Sync default student university
  useEffect(() => {
    if (selectedStudent?.universityId && (selectedUniId === 'ALL' || !selectedUniId)) {
      setSelectedUniId(selectedStudent.universityId);
    }
  }, [selectedStudent]);

  // Filter students for quick search dropdown
  const searchedStudents = useMemo(() => {
    if (!studentSearch.trim()) return [];
    const term = studentSearch.toLowerCase().trim();
    return students
      .filter(
        (s) =>
          s.fullName?.toLowerCase().includes(term) ||
          s.phoneNumber?.includes(term) ||
          s.universityName?.toLowerCase().includes(term)
      )
      .slice(0, 15);
  }, [students, studentSearch]);

  // Filter students for full directory overlay
  const directoryFilteredStudents = useMemo(() => {
    if (!directorySearch.trim()) return students;
    const term = directorySearch.toLowerCase().trim();
    return students.filter(
      (s) =>
        s.fullName?.toLowerCase().includes(term) ||
        s.phoneNumber?.includes(term) ||
        s.universityName?.toLowerCase().includes(term)
    );
  }, [students, directorySearch]);

  // Aggregate trip counts by date for the calendar
  const tripsByDate = useMemo(() => {
    const map: Record<string, { count: number; availableSeats: number; hasOutbound: boolean; hasReturn: boolean; minPrice: number }> = {};
    for (const t of trips) {
      if (t.status === 'CANCELLED') continue;
      if (selectedUniId && selectedUniId !== 'ALL' && t.universityId !== selectedUniId) continue;

      const isReturn = t.direction === 'RETURN';
      if (tripBookingMode === 'ONE_WAY_OUTBOUND' && isReturn) continue;
      if (tripBookingMode === 'ONE_WAY_RETURN' && !isReturn) continue;

      const d = t.date;
      if (!d) continue;
      if (!map[d]) {
        map[d] = { count: 0, availableSeats: 0, hasOutbound: false, hasReturn: false, minPrice: t.price || 0 };
      }
      map[d].count += 1;
      map[d].availableSeats += t.availableSeats || 0;
      if (isReturn) {
        map[d].hasReturn = true;
      } else {
        map[d].hasOutbound = true;
      }
      if (t.price && t.price < map[d].minPrice) map[d].minPrice = t.price;
    }
    return map;
  }, [trips, selectedUniId, tripBookingMode]);

  // Calendar Days Grid Generation
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth(); // 0-indexed

  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const totalDaysInMonth = lastDayOfMonth.getDate();

    // Map starting day of week to Saturday = 0
    const startDayOffset = (firstDayOfMonth.getDay() + 1) % 7;
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const todayStr = getTodayIso();

    const days: Array<{
      dateStr: string;
      dayNumber: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      hasTrips: boolean;
      tripsCount: number;
      hasBothWays: boolean;
      availSeats: number;
    }> = [];

    // Preceding month trailing days
    for (let i = startDayOffset - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const dateStr = formatLocalDate(month === 0 ? year - 1 : year, month === 0 ? 11 : month - 1, d);
      days.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        hasTrips: false,
        tripsCount: 0,
        hasBothWays: false,
        availSeats: 0,
      });
    }

    // Current month days
    for (let d = 1; d <= totalDaysInMonth; d++) {
      const dateStr = formatLocalDate(year, month, d);
      const info = tripsByDate[dateStr];
      days.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        hasTrips: !!info && info.count > 0,
        tripsCount: info?.count || 0,
        hasBothWays: !!info && info.hasOutbound && info.hasReturn,
        availSeats: info?.availableSeats || 0,
      });
    }

    // Trailing days to fill 7 columns
    const remaining = (7 - (days.length % 7)) % 7;
    for (let d = 1; d <= remaining; d++) {
      const dateStr = formatLocalDate(month === 11 ? year + 1 : year, month === 11 ? 0 : month + 1, d);
      days.push({
        dateStr,
        dayNumber: d,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        hasTrips: false,
        tripsCount: 0,
        hasBothWays: false,
        availSeats: 0,
      });
    }

    return days;
  }, [year, month, tripsByDate]);

  // Outbound trips for the selected date
  const availableOutboundTrips = useMemo(() => {
    return trips.filter((t) => {
      const matchUni = !selectedUniId || selectedUniId === 'ALL' || t.universityId === selectedUniId;
      const matchDir = !t.direction || t.direction === 'OUTBOUND';
      const matchDate = !selectedDate || t.date === selectedDate;
      const isNotCancelled = t.status !== 'CANCELLED';
      return matchUni && matchDir && matchDate && isNotCancelled;
    });
  }, [trips, selectedUniId, selectedDate]);

  // Return trips for the selected date
  const availableReturnTrips = useMemo(() => {
    return trips.filter((t) => {
      const matchUni = !selectedUniId || selectedUniId === 'ALL' || t.universityId === selectedUniId;
      const matchDir = t.direction === 'RETURN';
      const matchDate = !selectedDate || t.date === selectedDate;
      const isNotCancelled = t.status !== 'CANCELLED';
      return matchUni && matchDir && matchDate && isNotCancelled;
    });
  }, [trips, selectedUniId, selectedDate]);

  // Load manifest & occupied seats for Outbound trip
  useEffect(() => {
    if (!selectedTrip) {
      setOccupiedSeats([]);
      setSelectedSeatNumber(null);
      return;
    }

    let isMounted = true;
    setTripManifestLoading(true);

    adminBookingService
      .getTripPassengerManifest(selectedTrip)
      .then((res) => {
        if (!isMounted) return;
        const occupied = res.passengers.filter((p) => p.isBooked).map((p) => p.seatNumber);
        setOccupiedSeats(occupied);
        if (selectedSeatNumber && occupied.includes(selectedSeatNumber)) {
          setSelectedSeatNumber(null);
        }
      })
      .catch((err) => {
        console.warn('Could not load seats for outbound trip:', err);
      })
      .finally(() => {
        if (isMounted) setTripManifestLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedTrip]);

  // Load manifest & occupied seats for Return trip
  useEffect(() => {
    if (!selectedReturnTrip) {
      setReturnOccupiedSeats([]);
      setSelectedReturnSeatNumber(null);
      return;
    }

    let isMounted = true;
    setReturnTripManifestLoading(true);

    adminBookingService
      .getTripPassengerManifest(selectedReturnTrip)
      .then((res) => {
        if (!isMounted) return;
        const occupied = res.passengers.filter((p) => p.isBooked).map((p) => p.seatNumber);
        setReturnOccupiedSeats(occupied);
        if (selectedReturnSeatNumber && occupied.includes(selectedReturnSeatNumber)) {
          setSelectedReturnSeatNumber(null);
        }
      })
      .catch((err) => {
        console.warn('Could not load seats for return trip:', err);
      })
      .finally(() => {
        if (isMounted) setReturnTripManifestLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedReturnTrip]);

  // Month navigation handlers
  const handlePrevMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(getTodayIso());
    setSelectedTrip(null);
    setSelectedReturnTrip(null);
  };

  const formatMonthYearHeader = (d: Date): string => {
    const mName = ARABIC_MONTHS[d.getMonth()] || '';
    return `${mName} ${d.getFullYear()}`;
  };

  // Helper: Apply weekday patterns (e.g. Sat/Mon/Wed, Sun/Tue/Thu, full study week)
  const applyWeekdayPreset = (pattern: 'MW' | 'TT' | 'ALL_WEEK') => {
    const y = calendarMonth.getFullYear();
    const m = calendarMonth.getMonth();
    const lastDay = new Date(y, m + 1, 0).getDate();
    const newDates: string[] = [];

    for (let d = 1; d <= lastDay; d++) {
      const curDate = new Date(y, m, d);
      const dayOfWeek = curDate.getDay(); // 0 Sun, 1 Mon, 2 Tue, 3 Wed, 4 Thu, 5 Fri, 6 Sat
      if (pattern === 'MW') {
        // Sat (6), Mon (1), Wed (3)
        if (dayOfWeek === 6 || dayOfWeek === 1 || dayOfWeek === 3) {
          newDates.push(formatLocalDate(y, m, d));
        }
      } else if (pattern === 'TT') {
        // Sun (0), Tue (2), Thu (4)
        if (dayOfWeek === 0 || dayOfWeek === 2 || dayOfWeek === 4) {
          newDates.push(formatLocalDate(y, m, d));
        }
      } else if (pattern === 'ALL_WEEK') {
        // Sat through Thu (6, 0, 1, 2, 3, 4) - exclude Friday
        if (dayOfWeek !== 5) {
          newDates.push(formatLocalDate(y, m, d));
        }
      }
    }

    if (newDates.length > 0) {
      setSelectedDates(newDates);
      setSelectedDate(newDates[0]);
      setIsMultiMode(true);
    }
  };

  // Helper: Find next available seat for a trip avoiding duplicates in current queue
  const getAvailableSeatForTrip = (
    tripToBook: Trip,
    knownOccupied: number[],
    currentQueue: QueuedBookingItem[]
  ): number => {
    const total = tripToBook.totalSeats || tripToBook.capacity || 50;
    const queuedSeats = currentQueue
      .filter((item) => item.trip.id === tripToBook.id)
      .map((item) => item.seatNumber);

    const allTaken = new Set([...knownOccupied, ...queuedSeats]);

    for (let s = 1; s <= total; s++) {
      if (!allTaken.has(s)) {
        return s;
      }
    }
    return 1;
  };

  // Helper: Add a specific trip & time to queue
  const handleAddTripToQueue = (tripToAdd: Trip) => {
    const isOutbound = tripToAdd.direction !== 'RETURN';
    const chosenSeat =
      (isOutbound && selectedTrip?.id === tripToAdd.id && selectedSeatNumber) ||
      (!isOutbound && selectedReturnTrip?.id === tripToAdd.id && selectedReturnSeatNumber) ||
      getAvailableSeatForTrip(
        tripToAdd,
        isOutbound ? occupiedSeats : returnOccupiedSeats,
        bookingQueue
      );

    const newItem: QueuedBookingItem = {
      id: `queue-${tripToAdd.id}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      trip: tripToAdd,
      date: tripToAdd.date || selectedDate,
      direction: (tripToAdd.direction as 'OUTBOUND' | 'RETURN') || 'OUTBOUND',
      departureTime: tripToAdd.departureTime,
      universityName: tripToAdd.universityName,
      price: tripToAdd.price || 0,
      seatNumber: chosenSeat,
    };

    setBookingQueue((prev) => [...prev, newItem]);
  };

  // Helper: Repeat trip with matching departure time across all selected dates
  const handleAddTripToAllSelectedDates = (templateTrip: Trip) => {
    const newItems: QueuedBookingItem[] = [];
    let addedCount = 0;

    for (const dateStr of selectedDates) {
      const match =
        trips.find(
          (t) =>
            t.date === dateStr &&
            t.direction === templateTrip.direction &&
            t.departureTime === templateTrip.departureTime &&
            (!templateTrip.universityId || t.universityId === templateTrip.universityId) &&
            t.status !== 'CANCELLED'
        ) ||
        trips.find(
          (t) =>
            t.date === dateStr &&
            t.direction === templateTrip.direction &&
            (!templateTrip.universityId || t.universityId === templateTrip.universityId) &&
            t.status !== 'CANCELLED'
        );

      if (match) {
        const seat = getAvailableSeatForTrip(match, [], [...bookingQueue, ...newItems]);
        newItems.push({
          id: `queue-${match.id}-${dateStr}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          trip: match,
          date: dateStr,
          direction: (match.direction as 'OUTBOUND' | 'RETURN') || 'OUTBOUND',
          departureTime: match.departureTime,
          universityName: match.universityName,
          price: match.price || templateTrip.price || 0,
          seatNumber: seat,
        });
        addedCount++;
      }
    }

    if (newItems.length > 0) {
      setBookingQueue((prev) => [...prev, ...newItems]);
      alert(`تمت إضافة ${addedCount} مواعيد رحلات لكافة الأيام المحددة إلى قائمة الحجز!`);
    } else {
      alert(`لم يتم العثور على رحلات مجدولة مطابقة في التواريخ المحددة.`);
    }
  };

  // Helper: Add both outbound and return to queue for single or all selected dates
  const handleAddRoundTripToQueue = (forAllSelectedDates = false) => {
    if (!selectedTrip || !selectedReturnTrip) {
      alert('يرجى اختيار رحلة الذهاب ورحلة العودة أولاً');
      return;
    }

    if (!forAllSelectedDates) {
      const outSeat = selectedSeatNumber || getAvailableSeatForTrip(selectedTrip, occupiedSeats, bookingQueue);
      const retSeat = selectedReturnSeatNumber || getAvailableSeatForTrip(selectedReturnTrip, returnOccupiedSeats, bookingQueue);

      const itemOut: QueuedBookingItem = {
        id: `queue-${selectedTrip.id}-${Date.now()}-out`,
        trip: selectedTrip,
        date: selectedDate,
        direction: 'OUTBOUND',
        departureTime: selectedTrip.departureTime,
        universityName: selectedTrip.universityName,
        price: selectedTrip.price || 0,
        seatNumber: outSeat,
      };

      const itemRet: QueuedBookingItem = {
        id: `queue-${selectedReturnTrip.id}-${Date.now()}-ret`,
        trip: selectedReturnTrip,
        date: selectedDate,
        direction: 'RETURN',
        departureTime: selectedReturnTrip.departureTime,
        universityName: selectedReturnTrip.universityName,
        price: selectedReturnTrip.price || 0,
        seatNumber: retSeat,
      };

      setBookingQueue((prev) => [...prev, itemOut, itemRet]);
      return;
    }

    const newItems: QueuedBookingItem[] = [];
    let daysAdded = 0;

    for (const dateStr of selectedDates) {
      const outMatch =
        trips.find(
          (t) =>
            t.date === dateStr &&
            (t.direction === 'OUTBOUND' || !t.direction) &&
            t.departureTime === selectedTrip.departureTime &&
            t.universityId === selectedTrip.universityId &&
            t.status !== 'CANCELLED'
        ) ||
        trips.find(
          (t) =>
            t.date === dateStr &&
            (t.direction === 'OUTBOUND' || !t.direction) &&
            t.universityId === selectedTrip.universityId &&
            t.status !== 'CANCELLED'
        );

      const retMatch =
        trips.find(
          (t) =>
            t.date === dateStr &&
            t.direction === 'RETURN' &&
            t.departureTime === selectedReturnTrip.departureTime &&
            t.universityId === selectedReturnTrip.universityId &&
            t.status !== 'CANCELLED'
        ) ||
        trips.find(
          (t) =>
            t.date === dateStr &&
            t.direction === 'RETURN' &&
            t.universityId === selectedReturnTrip.universityId &&
            t.status !== 'CANCELLED'
        );

      if (outMatch) {
        const s = getAvailableSeatForTrip(outMatch, [], [...bookingQueue, ...newItems]);
        newItems.push({
          id: `queue-${outMatch.id}-${dateStr}-out-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          trip: outMatch,
          date: dateStr,
          direction: 'OUTBOUND',
          departureTime: outMatch.departureTime,
          universityName: outMatch.universityName,
          price: outMatch.price || selectedTrip.price || 0,
          seatNumber: s,
        });
      }

      if (retMatch) {
        const s = getAvailableSeatForTrip(retMatch, [], [...bookingQueue, ...newItems]);
        newItems.push({
          id: `queue-${retMatch.id}-${dateStr}-ret-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          trip: retMatch,
          date: dateStr,
          direction: 'RETURN',
          departureTime: retMatch.departureTime,
          universityName: retMatch.universityName,
          price: retMatch.price || selectedReturnTrip.price || 0,
          seatNumber: s,
        });
      }

      if (outMatch || retMatch) {
        daysAdded++;
      }
    }

    if (newItems.length > 0) {
      setBookingQueue((prev) => [...prev, ...newItems]);
      alert(`تمت إضافة ${newItems.length} رحلة ذهاب وعودة عبر ${daysAdded} أيام إلى قائمة الحجز!`);
    } else {
      alert(`لم يتم العثور على رحلات ذهاب وعودة مطابقة في التواريخ المحددة.`);
    }
  };

  const handleRemoveFromQueue = (itemId: string) => {
    setBookingQueue((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleClearQueue = () => {
    if (window.confirm('هل أنت متأكد من تفريغ كافة الرحلات المحددة؟')) {
      setBookingQueue([]);
    }
  };

  // Form submission handler (Handles both Multi-Queue and Single/Round-Trip booking)
  const handleConfirmBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) {
      alert('يرجى تحديد الطالب المسافر أولاً');
      return;
    }

    // 1. Multi-Queue Batch Booking
    if (bookingQueue.length > 0) {
      setIsSubmitting(true);
      try {
        if (editingAttempt) {
          await cleanupPreviousAttempt();
        }

        const groupId =
          editingAttempt?.bookingGroupId ||
          `GRP-${Date.now().toString(36).toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`;
        const groupCode =
          editingAttempt?.bookingCode ||
          editingAttempt?.bookingGroupCode ||
          `OTO-${Math.floor(10000 + Math.random() * 90000)}`;
        const nowIso = new Date().toISOString();

        const successfulBookings: Array<{
          tripDate: string;
          direction: string;
          departureTime: string;
          universityName: string;
          seatNumber: number;
          bookingCode: string;
          ticketId: string;
          price: number;
          tripId: string;
        }> = [];

        for (let i = 0; i < bookingQueue.length; i++) {
          const item = bookingQueue[i];
          const res = await adminBookingService.bookTripForStudent({
            student: selectedStudent,
            trip: item.trip,
            seatNumber: item.seatNumber,
            direction: item.direction,
            paymentMethod,
            isPaid,
            adminEmail,
            adminNote:
              (adminNote ? adminNote + ' - ' : '') +
              `حجز متعدد [${item.date} ${item.departureTime}] (${i + 1}/${bookingQueue.length})`,
            bookingGroupId: groupId,
            bookingGroupCode: groupCode,
            skipPaymentAttemptAndTreasury: true,
          });

          if (res.success && res.booking) {
            successfulBookings.push({
              tripDate: item.date,
              direction: item.direction,
              departureTime: item.departureTime,
              universityName: item.universityName,
              seatNumber: item.seatNumber,
              bookingCode: res.booking.bookingCode,
              ticketId: res.booking.ticketId,
              price: item.price,
              tripId: item.trip.id,
            });
          }
        }

        if (successfulBookings.length > 0) {
          const totalPrice = successfulBookings.reduce((sum, b) => sum + b.price, 0);
          const paymentStatus: PaymentStatus = isPaid
            ? 'PAID'
            : (paymentMethod === 'BRANCH' ? 'AWAITING_BRANCH_PAYMENT' : 'PENDING_VERIFICATION');

          // Save bookingGroup
          const groupDoc = {
            id: groupId,
            groupCode,
            studentId: selectedStudent.uid,
            studentSnapshot: {
              uid: selectedStudent.uid,
              fullName: selectedStudent.fullName,
              email: selectedStudent.email,
              phoneNumber: selectedStudent.phoneNumber,
              address: selectedStudent.address,
              universityName: selectedStudent.universityName,
              universityId: selectedStudent.universityId,
            },
            itemCount: successfulBookings.length,
            bookingIds: successfulBookings.map((b) => b.ticketId || b.bookingCode),
            bookings: successfulBookings.map((b) => ({
              bookingCode: b.bookingCode,
              ticketId: b.ticketId,
              tripId: b.tripId,
              date: b.tripDate,
              time: b.departureTime,
              seatNumber: b.seatNumber,
              direction: b.direction,
              price: b.price,
              universityName: b.universityName,
            })),
            tripDates: [...new Set(successfulBookings.map((b) => b.tripDate))],
            totalAmount: totalPrice,
            currency: 'EGP',
            paymentMethod,
            paymentStatus,
            status: isPaid ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION',
            bookingStatus: isPaid ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION',
            createdAt: nowIso,
            updatedAt: nowIso,
          };

          try {
            await setDoc(doc(db, 'bookingGroups', groupId), groupDoc);
          } catch (e) {
            console.warn('Firestore setDoc bookingGroup error:', e);
          }

          if (typeof window !== 'undefined') {
            try {
              sessionStorage.setItem(`nashgo_dev_group_${groupId}`, JSON.stringify(groupDoc));
              localStorage.setItem(`nashgo_dev_group_${groupId}`, JSON.stringify(groupDoc));
            } catch (_) {}
          }

          // Consolidated Payment Attempt
          const attemptId =
            editingAttempt?.id ||
            `ATT-${groupCode.replace(/[^A-Z0-9]/g, '')}`;
          const attemptDoc: PaymentAttempt = {
            id: attemptId,
            bookingGroupId: groupId,
            bookingGroupCode: groupCode,
            bookingCode: groupCode,
            studentId: selectedStudent.uid,
            studentName: selectedStudent.fullName,
            studentPhone: selectedStudent.phoneNumber,
            universityName: selectedStudent.universityName || successfulBookings[0].universityName,
            tripDate: [...new Set(successfulBookings.map((b) => b.tripDate))].join(' ، '),
            seatNumber: successfulBookings.map((b) => '#' + b.seatNumber).join(', ') as any,
            amount: totalPrice,
            currency: 'EGP',
            paymentMethod,
            provider: 'MANUAL',
            status: paymentStatus,
            adminNote: (adminNote ? adminNote + ' - ' : '') + `حجز باقة رحلات (${successfulBookings.length} رحلات) بواسطة الإدارة`,
            createdAt: nowIso,
            updatedAt: nowIso,
            expiresAt: new Date(Date.now() + 86400000).toISOString(),
          };

          if (isPaid) {
            attemptDoc.reviewedAt = nowIso;
            attemptDoc.reviewedBy = adminEmail || 'مدير النظام';
          }

          try {
            await setDoc(doc(db, 'paymentAttempts', attemptId), attemptDoc);
          } catch (e) {
            console.warn('Firestore setDoc paymentAttempt error:', e);
          }

          if (typeof window !== 'undefined') {
            try {
              const rawAttempts =
                localStorage.getItem('oto_dev_payment_attempts') ||
                sessionStorage.getItem('oto_dev_payment_attempts') ||
                '[]';
              const attemptsList: PaymentAttempt[] = JSON.parse(rawAttempts);
              const existingIdx = attemptsList.findIndex((a) => a.id === attemptId);
              if (existingIdx >= 0) {
                attemptsList[existingIdx] = attemptDoc;
              } else {
                attemptsList.unshift(attemptDoc);
              }
              localStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
              sessionStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
            } catch (_) {}
          }

          // If "تم التحصيل", immediately record income in Treasury!
          const wasAlreadyPaid = editingAttempt && editingAttempt.status === 'PAID';
          if (isPaid && totalPrice > 0 && !wasAlreadyPaid) {
            try {
              const vault = mapPaymentMethodToVault(paymentMethod);
              await treasuryService.addTransaction({
                type: 'INCOME',
                category: 'BOOKING_PAYMENT',
                categoryLabelAr: 'تحصيل حجز باقة رحلات طالب (إدارة)',
                amount: totalPrice,
                vault,
                description: `تحصيل مباشر لحجز باقة (${successfulBookings.length} رحلات) للطالب (${selectedStudent.fullName}) - كود ${groupCode}`,
                referenceNumber: groupCode,
                recordedBy: adminEmail || 'مدير النظام (تحصيل مباشر)',
                date: nowIso,
                status: 'CONFIRMED',
              });
            } catch (tErr) {
              console.warn('Could not record batch booking in treasury:', tErr);
            }
          } else if (isPaid && wasAlreadyPaid && totalPrice !== (editingAttempt.amount || 0)) {
            const diff = totalPrice - (editingAttempt.amount || 0);
            if (diff !== 0) {
              try {
                const vault = mapPaymentMethodToVault(paymentMethod);
                await treasuryService.addTransaction({
                  type: diff > 0 ? 'INCOME' : 'EXPENSE',
                  category: 'BOOKING_PAYMENT',
                  categoryLabelAr: diff > 0 ? 'فارق تحصيل تعديل حجز طالب' : 'رد فارق تعديل حجز طالب',
                  amount: Math.abs(diff),
                  vault,
                  description: `تسوية فارق تعديل حجز باقة (${groupCode}) للطالب (${selectedStudent.fullName})`,
                  referenceNumber: groupCode,
                  recordedBy: adminEmail || 'مدير النظام (تعديل حجز)',
                  date: nowIso,
                  status: 'CONFIRMED',
                });
              } catch (tErr) {
                console.warn('Could not record treasury adjustment:', tErr);
              }
            }
          }

          let msg = `مرحباً يا ${selectedStudent.fullName}، تم حجز (${successfulBookings.length}) رحلات ومواعيد لك بنجاح بواسطة إدارة OTO Students!\n\n📋 تفاصيل الرحلات المحجوزة:\n`;
          successfulBookings.forEach((b, idx) => {
            msg += `${idx + 1}. ${b.direction}: ${b.tripDate} (${getDayOfWeekName(b.tripDate)}) الساعة ${b.departureTime}\n   - الوجهة: ${b.universityName}\n   - رقم المقعد: #${b.seatNumber}\n   - كود الحجز: ${b.bookingCode}\n   - رقم التذكرة: ${b.ticketId}\n\n`;
          });
          msg += `💰 إجمالي المبلغ: ${totalPrice} ج.م\nالحالة: ${isPaid ? 'مدفوع ومؤكد' : 'في انتظار السداد'}\n\nنتمنى لك فصلاً دراسياً موفقاً ورحلات آمنة دائماً مع OTO!`;

          const waUrl = adminBookingService.getWhatsAppUrl(selectedStudent.phoneNumber, msg);

          setSuccessBookingResult({
            isMulti: true,
            totalTripsCount: successfulBookings.length,
            totalPrice,
            multiBookings: successfulBookings,
            isRoundTrip: false,
            bookingCode: groupCode,
            ticketId: successfulBookings[0].ticketId,
            studentPhone: selectedStudent.phoneNumber,
            whatsAppUrl: waUrl,
          });

          setBookingQueue([]);
          onSuccess(
            editingAttempt
              ? `تم بنجاح تحديث وتعديل حجز الطالب (${groupCode}) وتحديث التذاكر!`
              : `تم بنجاح تأكيد حجز ${successfulBookings.length} رحلة وتوليد التذاكر!`
          );
        }
      } catch (err: any) {
        alert(`حدث خطأ أثناء حجز الرحلات: ${err?.message || 'خطأ غير معروف'}`);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // 2. Single-Day Round-Trip Booking
    if (tripBookingMode === 'ROUND_TRIP') {
      if (!selectedTrip) {
        alert('يرجى اختيار رحلة الذهاب (صباحاً)');
        return;
      }
      if (!selectedSeatNumber) {
        alert('يرجى اختيار رقم مقعد رحلة الذهاب');
        return;
      }
      if (!selectedReturnTrip) {
        alert('يرجى اختيار رحلة العودة (مساءً)');
        return;
      }
      if (!selectedReturnSeatNumber) {
        alert('يرجى اختيار رقم مقعد رحلة العودة');
        return;
      }

      setIsSubmitting(true);
      try {
        if (editingAttempt) {
          await cleanupPreviousAttempt();
        }

        const groupId =
          editingAttempt?.bookingGroupId ||
          `GRP-${Date.now().toString(36).toUpperCase()}-${Math.floor(100 + Math.random() * 900)}`;
        const groupCode =
          editingAttempt?.bookingCode ||
          editingAttempt?.bookingGroupCode ||
          `OTO-${Math.floor(10000 + Math.random() * 90000)}`;
        const nowIso = new Date().toISOString();
        const totalPrice = (selectedTrip.price || 0) + (selectedReturnTrip.price || 0);

        // Book Outbound Trip
        const resOut = await adminBookingService.bookTripForStudent({
          student: selectedStudent,
          trip: selectedTrip,
          seatNumber: selectedSeatNumber,
          direction: 'OUTBOUND',
          paymentMethod,
          isPaid,
          adminEmail,
          adminNote: (adminNote ? adminNote + ' - ' : '') + 'ذهاب وعودة في نفس اليوم (تذكرة الذهاب)',
          bookingGroupId: groupId,
          bookingGroupCode: groupCode,
          skipPaymentAttemptAndTreasury: true,
        });

        // Book Return Trip
        const resRet = await adminBookingService.bookTripForStudent({
          student: selectedStudent,
          trip: selectedReturnTrip,
          seatNumber: selectedReturnSeatNumber,
          direction: 'RETURN',
          paymentMethod,
          isPaid,
          adminEmail,
          adminNote: (adminNote ? adminNote + ' - ' : '') + 'ذهاب وعودة في نفس اليوم (تذكرة العودة)',
          bookingGroupId: groupId,
          bookingGroupCode: groupCode,
          skipPaymentAttemptAndTreasury: true,
        });

        if (resOut.success && resRet.success) {
          const paymentStatus: PaymentStatus = isPaid
            ? 'PAID'
            : (paymentMethod === 'BRANCH' ? 'AWAITING_BRANCH_PAYMENT' : 'PENDING_VERIFICATION');

          // Save bookingGroup
          const groupDoc = {
            id: groupId,
            groupCode,
            studentId: selectedStudent.uid,
            studentSnapshot: {
              uid: selectedStudent.uid,
              fullName: selectedStudent.fullName,
              email: selectedStudent.email,
              phoneNumber: selectedStudent.phoneNumber,
              address: selectedStudent.address,
              universityName: selectedStudent.universityName,
              universityId: selectedStudent.universityId,
            },
            itemCount: 2,
            bookingIds: [resOut.booking.ticketId, resRet.booking.ticketId],
            bookings: [
              {
                bookingCode: resOut.booking.bookingCode,
                ticketId: resOut.booking.ticketId,
                tripId: selectedTrip.id,
                date: selectedTrip.date,
                time: selectedTrip.departureTime,
                seatNumber: selectedSeatNumber,
                direction: 'OUTBOUND',
                price: selectedTrip.price || 0,
                universityName: selectedTrip.universityName,
              },
              {
                bookingCode: resRet.booking.bookingCode,
                ticketId: resRet.booking.ticketId,
                tripId: selectedReturnTrip.id,
                date: selectedReturnTrip.date,
                time: selectedReturnTrip.departureTime,
                seatNumber: selectedReturnSeatNumber,
                direction: 'RETURN',
                price: selectedReturnTrip.price || 0,
                universityName: selectedReturnTrip.universityName,
              },
            ],
            tripDates: [selectedTrip.date],
            totalAmount: totalPrice,
            currency: 'EGP',
            paymentMethod,
            paymentStatus,
            status: isPaid ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION',
            bookingStatus: isPaid ? 'CONFIRMED' : 'PENDING_PAYMENT_VERIFICATION',
            createdAt: nowIso,
            updatedAt: nowIso,
          };

          try {
            await setDoc(doc(db, 'bookingGroups', groupId), groupDoc);
          } catch (e) {
            console.warn('Firestore setDoc bookingGroup error:', e);
          }

          if (typeof window !== 'undefined') {
            try {
              sessionStorage.setItem(`nashgo_dev_group_${groupId}`, JSON.stringify(groupDoc));
              localStorage.setItem(`nashgo_dev_group_${groupId}`, JSON.stringify(groupDoc));
            } catch (_) {}
          }

          // Consolidated Payment Attempt
          const attemptId =
            editingAttempt?.id ||
            `ATT-${groupCode.replace(/[^A-Z0-9]/g, '')}`;
          const attemptDoc: PaymentAttempt = {
            id: attemptId,
            bookingGroupId: groupId,
            bookingGroupCode: groupCode,
            bookingCode: groupCode,
            studentId: selectedStudent.uid,
            studentName: selectedStudent.fullName,
            studentPhone: selectedStudent.phoneNumber,
            universityName: selectedStudent.universityName || selectedTrip.universityName,
            tripDate: `${selectedTrip.date} (ذهاب وعودة)`,
            seatNumber: `#${selectedSeatNumber} ذهاب ، #${selectedReturnSeatNumber} عودة` as any,
            amount: totalPrice,
            currency: 'EGP',
            paymentMethod,
            provider: 'MANUAL',
            status: paymentStatus,
            adminNote: (adminNote ? adminNote + ' - ' : '') + 'حجز ذهاب وعودة في نفس اليوم بواسطة الإدارة',
            createdAt: nowIso,
            updatedAt: nowIso,
            expiresAt: new Date(Date.now() + 86400000).toISOString(),
          };

          if (isPaid) {
            attemptDoc.reviewedAt = nowIso;
            attemptDoc.reviewedBy = adminEmail || 'مدير النظام';
          }

          try {
            await setDoc(doc(db, 'paymentAttempts', attemptId), attemptDoc);
          } catch (e) {
            console.warn('Firestore setDoc paymentAttempt error:', e);
          }

          if (typeof window !== 'undefined') {
            try {
              const rawAttempts =
                localStorage.getItem('oto_dev_payment_attempts') ||
                sessionStorage.getItem('oto_dev_payment_attempts') ||
                '[]';
              const attemptsList: PaymentAttempt[] = JSON.parse(rawAttempts);
              const existingIdx = attemptsList.findIndex((a) => a.id === attemptId);
              if (existingIdx >= 0) {
                attemptsList[existingIdx] = attemptDoc;
              } else {
                attemptsList.unshift(attemptDoc);
              }
              localStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
              sessionStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
            } catch (_) {}
          }

          // If "تم التحصيل", immediately record income in Treasury!
          const wasAlreadyPaid = editingAttempt && editingAttempt.status === 'PAID';
          if (isPaid && totalPrice > 0 && !wasAlreadyPaid) {
            try {
              const vault = mapPaymentMethodToVault(paymentMethod);
              await treasuryService.addTransaction({
                type: 'INCOME',
                category: 'BOOKING_PAYMENT',
                categoryLabelAr: 'تحصيل حجز رحلة طالب (إدارة)',
                amount: totalPrice,
                vault,
                description: `تحصيل مباشر لحجز ذهاب وعودة (${selectedTrip.date}) للطالب (${selectedStudent.fullName}) - كود ${groupCode}`,
                referenceNumber: groupCode,
                recordedBy: adminEmail || 'مدير النظام (تحصيل مباشر)',
                date: nowIso,
                status: 'CONFIRMED',
              });
            } catch (tErr) {
              console.warn('Could not record round-trip in treasury:', tErr);
            }
          } else if (isPaid && wasAlreadyPaid && totalPrice !== (editingAttempt.amount || 0)) {
            const diff = totalPrice - (editingAttempt.amount || 0);
            if (diff !== 0) {
              try {
                const vault = mapPaymentMethodToVault(paymentMethod);
                await treasuryService.addTransaction({
                  type: diff > 0 ? 'INCOME' : 'EXPENSE',
                  category: 'BOOKING_PAYMENT',
                  categoryLabelAr: diff > 0 ? 'فارق تحصيل تعديل حجز طالب' : 'رد فارق تعديل حجز طالب',
                  amount: Math.abs(diff),
                  vault,
                  description: `تسوية فارق تعديل حجز (${groupCode}) للطالب (${selectedStudent.fullName})`,
                  referenceNumber: groupCode,
                  recordedBy: adminEmail || 'مدير النظام (تعديل حجز)',
                  date: nowIso,
                  status: 'CONFIRMED',
                });
              } catch (tErr) {
                console.warn('Could not record treasury adjustment:', tErr);
              }
            }
          }

          const msg = `مرحباً يا ${selectedStudent.fullName}، تم حجز رحلتك (ذهاب وعودة في نفس اليوم) بنجاح بواسطة إدارة OTO Students!\n\nتفاصيل رحلة الذهاب ☀️:\n- الوجهة: ${selectedTrip.universityName}\n- الموعد: ${selectedTrip.date} الساعة ${selectedTrip.departureTime}\n- رقم المقعد: #${selectedSeatNumber}\n- كود الحجز: ${resOut.booking.bookingCode}\n- رقم التذكرة: ${resOut.booking.ticketId}\n\nتفاصيل رحلة العودة 🌙:\n- الوجهة: الإسكندرية (${selectedReturnTrip.universityName})\n- الموعد: ${selectedReturnTrip.date} الساعة ${selectedReturnTrip.departureTime}\n- رقم المقعد: #${selectedReturnSeatNumber}\n- كود الحجز: ${resRet.booking.bookingCode}\n- رقم التذكرة: ${resRet.booking.ticketId}\n\nالحالة: ${isPaid ? 'مدفوع ومؤكد' : 'في انتظار السداد'}\nنتمنى لك يوماً دراسياً موفقاً ورحلة آمنة مع OTO!`;
          const waUrl = adminBookingService.getWhatsAppUrl(selectedStudent.phoneNumber, msg);

          setSuccessBookingResult({
            isRoundTrip: true,
            bookingCode: groupCode,
            ticketId: resOut.booking.ticketId,
            studentPhone: selectedStudent.phoneNumber,
            whatsAppUrl: waUrl,
            returnBookingCode: resRet.booking.bookingCode,
            returnTicketId: resRet.booking.ticketId,
            returnSeatNumber: selectedReturnSeatNumber,
          });

          onSuccess(
            editingAttempt
              ? `تم بنجاح تحديث وتعديل رحلتي الذهاب والعودة للطالب (${groupCode}) وتحديث التذاكر!`
              : 'تم تأكيد حجز رحلتي الذهاب والعودة بنجاح وتوليد التذاكر!'
          );
        }
      } catch (err: any) {
        alert(`حدث خطأ أثناء إتمام الحجز: ${err?.message || 'خطأ غير معروف'}`);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // 3. Single Trip (Outbound or Return)
    if (!selectedTrip) {
      alert('يرجى اختيار الرحلة المطلوبة');
      return;
    }
    if (!selectedSeatNumber) {
      alert('يرجى اختيار رقم المقعد من خريطة الحافلة');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingAttempt) {
        await cleanupPreviousAttempt();
      }

      const res = await adminBookingService.bookTripForStudent({
        student: selectedStudent,
        trip: selectedTrip,
        seatNumber: selectedSeatNumber,
        paymentMethod,
        isPaid,
        adminEmail,
        adminNote,
        bookingGroupCode: (editingAttempt?.bookingCode || editingAttempt?.bookingGroupCode) || undefined,
      });

      if (res.success) {
        const msg = `مرحباً يا ${selectedStudent.fullName}، تم حجز رحلتك بنجاح بواسطة إدارة OTO Students!\n\nتفاصيل الرحلة:\n- الوجهة: ${selectedTrip.universityName}\n- الموعد: ${selectedTrip.date} الساعة ${selectedTrip.departureTime}\n- رقم المقعد: #${selectedSeatNumber}\n- كود الحجز: ${res.booking.bookingCode}\n- رقم التذكرة: ${res.booking.ticketId}\n- الحالة: ${isPaid ? 'مدفوع ومؤكد' : 'في انتظار السداد'}\n\nنتمنى لك رحلة موفقة وآمنة مع OTO!`;
        const waUrl = adminBookingService.getWhatsAppUrl(selectedStudent.phoneNumber, msg);

        setSuccessBookingResult({
          isRoundTrip: false,
          bookingCode: res.booking.bookingCode,
          ticketId: res.booking.ticketId,
          studentPhone: selectedStudent.phoneNumber,
          whatsAppUrl: waUrl,
        });

        onSuccess(
          editingAttempt
            ? `تم بنجاح تحديث وتعديل رحلة الطالب (${res.booking.bookingCode}) وتحديث التذكرة!`
            : res.message
        );
      }
    } catch (err: any) {
      alert(`حدث خطأ أثناء إتمام الحجز: ${err?.message || 'خطأ غير معروف'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reusable Bus Seat Grid Renderer
  const renderSeatMap = (
    trip: Trip,
    currentSeat: number | null,
    onSelectSeat: (seat: number) => void,
    occupiedList: number[],
    isLoading: boolean,
    titleLabel: string
  ) => {
    const total = trip.totalSeats || trip.capacity || 50;

    return (
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          padding: '14px',
          marginTop: '10px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Armchair size={16} className="text-[#E5232A]" />
            <span>{titleLabel} ({total <= 14 ? 'تويوتا هاي إس 14 راكب' : 'حافلة سياحية 50 راكب'})</span>
          </span>
          {currentSeat && (
            <span style={{ fontSize: '12px', fontWeight: 'bold', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '2px 8px', borderRadius: '999px' }}>
              المقعد المختار: #{currentSeat}
            </span>
          )}
        </div>

        <div className="bus-seat-layout-card">
          <div className="bus-front-cab" style={{ marginBottom: '8px' }}>
            <span>كابينة السائق ومقدمة الحافلة 🚍</span>
            <span>إجمالي السعة: {total} مقعد</span>
          </div>

          {isLoading ? (
            <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
              جاري فحص المقاعد الشاغرة للحافلة...
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
                gap: '6px',
                padding: '8px 0',
              }}
            >
              {Array.from({ length: total }).map((_, idx) => {
                const sNum = idx + 1;
                const isOccupied = occupiedList.includes(sNum);
                const isSelected = currentSeat === sNum;

                return (
                  <button
                    key={sNum}
                    type="button"
                    disabled={isOccupied}
                    onClick={() => onSelectSeat(sNum)}
                    style={{
                      height: '48px',
                      borderRadius: '8px',
                      border: isSelected
                        ? '2px solid #E5232A'
                        : isOccupied
                        ? '1px solid #fecaca'
                        : '1px solid #cbd5e1',
                      backgroundColor: isSelected
                        ? '#E5232A'
                        : isOccupied
                        ? '#fee2e2'
                        : '#f8fafc',
                      color: isSelected
                        ? '#ffffff'
                        : isOccupied
                        ? '#991b1b'
                        : '#1e293b',
                      cursor: isOccupied ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.12s ease',
                      boxShadow: isSelected ? '0 2px 8px rgba(229, 35, 42, 0.3)' : 'none',
                    }}
                    title={isOccupied ? `المقعد #${sNum} محجوز` : `اختيار المقعد #${sNum}`}
                  >
                    <span style={{ fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace' }}>
                      #{sNum}
                    </span>
                    <span style={{ fontSize: '9px', fontWeight: 'normal', opacity: 0.85 }}>
                      {isOccupied ? 'محجوز' : isSelected ? 'محدد ✓' : 'متاح'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Reusable Trip Card Renderer
  const renderTripCard = (t: Trip, isSelected: boolean, onSelect: () => void) => {
    const avail = t.availableSeats || 0;
    const isFull = avail <= 0;
    const queuedCount = bookingQueue.filter((item) => item.trip.id === t.id).length;

    return (
      <div
        key={t.id}
        style={{
          padding: '12px 14px',
          borderRadius: '14px',
          border: isSelected
            ? '2px solid #E5232A'
            : queuedCount > 0
            ? '2px solid #10b981'
            : '1px solid #e2e8f0',
          backgroundColor: queuedCount > 0 ? '#f0fdf4' : '#ffffff',
          transition: 'all 0.15s ease',
          boxShadow: isSelected ? '0 4px 12px rgba(229, 35, 42, 0.12)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div onClick={onSelect} style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={15} className="text-[#E5232A]" />
              <strong style={{ fontSize: '14px', fontFamily: 'monospace', color: '#0f172a' }}>
                {t.departureTime}
              </strong>
              {queuedCount > 0 && (
                <span
                  style={{
                    fontSize: '10px',
                    backgroundColor: '#dcfce7',
                    color: '#15803d',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                  }}
                >
                  مضاف ({queuedCount}) ✓
                </span>
              )}
            </div>
            <span style={{ fontSize: '13px', fontWeight: '800', color: '#059669', fontFamily: 'monospace' }}>
              {t.price} ج.م
            </span>
          </div>

          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '6px' }}>
            {t.universityName}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', marginBottom: '8px' }}>
            <span style={{ color: '#64748b' }}>
              {t.direction === 'RETURN' ? 'عودة للإسكندرية 🌙' : 'ذهاب للجامعة ☀️'}
            </span>
            <span
              style={{
                fontWeight: 'bold',
                color: isFull ? '#dc2626' : avail <= 3 ? '#d97706' : '#16a34a',
                backgroundColor: isFull ? '#fee2e2' : avail <= 3 ? '#fef3c7' : '#dcfce7',
                padding: '2px 6px',
                borderRadius: '6px',
              }}
            >
              {isFull ? 'مكتمل' : `${avail} مقعد متاح`}
            </span>
          </div>
        </div>

        {/* Action Buttons: Add to queue / Repeat across all dates */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleAddTripToQueue(t);
            }}
            style={{
              flex: 1,
              padding: '5px 8px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#f8fafc',
              color: '#0f172a',
              fontSize: '11px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
            title="إضافة هذا الميعاد إلى قائمة الحجز"
          >
            <Plus size={12} className="text-[#E5232A]" />
            <span>أضف للقائمة</span>
          </button>

          {isMultiMode && selectedDates.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAddTripToAllSelectedDates(t);
              }}
              style={{
                flex: 1,
                padding: '5px 8px',
                borderRadius: '8px',
                border: '1px solid #fbcfe8',
                backgroundColor: '#fdf2f8',
                color: '#db2777',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '3px',
              }}
              title="تكرار هذا الميعاد لكافة الأيام المحددة"
            >
              <RefreshCw size={11} />
              <span>لكل الأيام ({selectedDates.length})</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose} dir="rtl">
      <div
        className="admin-modal-card relative"
        style={{
          width: '96vw',
          maxWidth: '1380px',
          height: '93vh',
          maxHeight: '93vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '24px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Top Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#ffffff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                backgroundColor: '#FFF1F2',
                color: '#E5232A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RefreshCw size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                {modalTitle || 'حجز رحلة وتذكرة جديدة لطالب مسجل'}
              </h2>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                {modalSubtitle || 'اختيار الطالب، تصفح المواعيد والكلندر الشهري، حجز ذهاب وعودة في نفس اليوم، وتعيين المقاعد فوري'}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="admin-modal-close"
            onClick={onClose}
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#64748b',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Success Feedback Screen */}
        {successBookingResult ? (
          <div
            style={{
              padding: '40px 24px',
              textAlign: 'center',
              margin: 'auto',
              maxWidth: '560px',
              width: '100%',
            }}
          >
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: '#dcfce7',
                color: '#16a34a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 18px',
              }}
            >
              <CheckCircle2 size={40} />
            </div>

            <h3 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>
              {successBookingResult.isMulti
                ? `تم حجز وتأكيد (${successBookingResult.totalTripsCount}) رحلات ومواعيد بنجاح! 🎉`
                : successBookingResult.isRoundTrip
                ? 'تم حجز رحلتي الذهاب والعودة بنجاح! 🎉'
                : 'تم تأكيد الحجز وتوليد التذكرة بنجاح! 🎉'}
            </h3>

            <p style={{ fontSize: '13px', color: '#475569', marginBottom: '22px' }}>
              {successBookingResult.isMulti
                ? 'تم تسجيل كافة الحجوزات والمواعيد بقاعدة البيانات وتحديث سعة الحافلات وتوليد التذاكر.'
                : 'تم تسجيل الحجز في قاعدة البيانات وتحديث سعة المقاعد بالحافلة.'}
            </p>

            <div
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                padding: '16px 20px',
                textAlign: 'right',
                marginBottom: '24px',
                maxHeight: '340px',
                overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2e8f0', fontSize: '13px' }}>
                <span style={{ color: '#64748b' }}>اسم الطالب:</span>
                <strong style={{ color: '#0f172a' }}>{selectedStudent?.fullName}</strong>
              </div>

              {successBookingResult.isMulti && successBookingResult.multiBookings ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2e8f0', fontSize: '13px' }}>
                    <span style={{ color: '#64748b' }}>إجمالي التذاكر المحجوزة:</span>
                    <strong style={{ color: '#16a34a' }}>{successBookingResult.totalTripsCount} رحلات</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2e8f0', fontSize: '13px' }}>
                    <span style={{ color: '#64748b' }}>إجمالي المبلغ المدفوع:</span>
                    <strong style={{ color: '#E5232A', fontFamily: 'monospace' }}>{successBookingResult.totalPrice} ج.م</strong>
                  </div>

                  <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {successBookingResult.multiBookings.map((b, idx) => (
                      <div
                        key={idx}
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '10px',
                          padding: '10px 12px',
                          border: '1px solid #e2e8f0',
                          fontSize: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <strong style={{ color: '#0f172a' }}>
                            {idx + 1}. {b.direction} ({b.tripDate})
                          </strong>
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#E5232A' }}>
                            {b.departureTime}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', fontSize: '11px' }}>
                          <span>{b.universityName} | مقعد #{b.seatNumber}</span>
                          <span>كود: <strong style={{ color: '#0f172a', fontFamily: 'monospace' }}>{b.bookingCode}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Outbound Ticket Info */}
                  <div style={{ padding: '8px 0', borderBottom: successBookingResult.isRoundTrip ? '1px solid #e2e8f0' : 'none' }}>
                    {successBookingResult.isRoundTrip && (
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#d97706', marginBottom: '4px' }}>
                        ☀️ تذكرة رحلة الذهاب:
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#64748b' }}>كود الحجز: <strong style={{ color: '#E5232A', fontFamily: 'monospace' }}>{successBookingResult.bookingCode}</strong></span>
                      <span style={{ color: '#64748b' }}>رقم المقعد: <strong style={{ color: '#059669' }}>#{selectedSeatNumber}</strong></span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                      رقم التذكرة: <span style={{ fontFamily: 'monospace', color: '#0f172a' }}>{successBookingResult.ticketId}</span>
                    </div>
                  </div>

                  {/* Return Ticket Info (if round-trip) */}
                  {successBookingResult.isRoundTrip && (
                    <div style={{ padding: '8px 0' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#4f46e5', marginBottom: '4px' }}>
                        🌙 تذكرة رحلة العودة:
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#64748b' }}>كود الحجز: <strong style={{ color: '#E5232A', fontFamily: 'monospace' }}>{successBookingResult.returnBookingCode}</strong></span>
                        <span style={{ color: '#64748b' }}>رقم المقعد: <strong style={{ color: '#059669' }}>#{successBookingResult.returnSeatNumber}</strong></span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        رقم التذكرة: <span style={{ fontFamily: 'monospace', color: '#0f172a' }}>{successBookingResult.returnTicketId}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {successBookingResult.whatsAppUrl && (
                <a
                  href={successBookingResult.whatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-admin-primary"
                  style={{
                    backgroundColor: '#25D366',
                    borderColor: '#25D366',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    textDecoration: 'none',
                  }}
                >
                  <MessageSquare size={18} />
                  <span>
                    {successBookingResult.isMulti
                      ? `إرسال كافة التذاكر (${successBookingResult.totalTripsCount}) عبر واتساب فوراً`
                      : 'إرسال التذكرة للطالب عبر واتساب فوراً'}
                  </span>
                  <ExternalLink size={14} />
                </a>
              )}

              <button type="button" className="btn-admin-outline" onClick={onClose}>
                إغلاق والعودة للسجل
              </button>
            </div>
          </div>
        ) : (
          /* Main Interactive Booking Grid */
          <form
            onSubmit={handleConfirmBooking}
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(380px, 1fr) minmax(0, 1.65fr)',
                flex: 1,
                overflowY: 'auto',
                gap: '0px',
              }}
            >
              {/* RIGHT COLUMN (RTL Start): Student Selection & Payment Details */}
              <div
                style={{
                  order: 1,
                  padding: '20px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '18px',
                  backgroundColor: '#ffffff',
                  overflowY: 'auto',
                }}
              >
                {/* STEP 1: Select Student Card or Search */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <User size={18} className="text-[#E5232A]" />
                      <span>الخطوة 1: تحديد الطالب المسجل</span>
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        setShowStudentDirectory(true);
                        setDirectorySearch('');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#f8fafc',
                        color: '#334155',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                      }}
                    >
                      <Users size={13} />
                      <span>تصفح سجل الطلاب بالكامل</span>
                    </button>
                  </div>

                  {selectedStudent ? (
                    /* Selected Student Profile Box (Name, Phone, University ONLY) */
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: '16px',
                        backgroundColor: '#FFF1F2',
                        border: '1.5px solid #FECDD3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '50%',
                            backgroundColor: '#E5232A',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '16px',
                            flexShrink: 0,
                          }}
                        >
                          {selectedStudent.fullName.charAt(0) || 'ط'}
                        </div>
                        <div>
                          {/* 1. Name */}
                          <strong style={{ fontSize: '14px', color: '#0f172a', display: 'block', marginBottom: '3px' }}>
                            {selectedStudent.fullName}
                          </strong>

                          {/* 2. Phone & 3. University ONLY */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontSize: '12px', color: '#475569' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'monospace', fontWeight: 'bold' }} dir="ltr">
                              <Phone size={12} className="text-[#E5232A]" />
                              {selectedStudent.phoneNumber || '—'}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <GraduationCap size={13} className="text-slate-500" />
                              <span>{selectedStudent.universityName || 'جامعة غير محددة'}</span>
                            </span>
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => setSelectedStudent(null)}
                        style={{
                          padding: '5px 10px',
                          borderRadius: '8px',
                          border: '1px solid #fca5a5',
                          backgroundColor: '#ffffff',
                          color: '#dc2626',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                      >
                        تغيير الطالب
                      </button>
                    </div>
                  ) : (
                    /* Search Input and Quick Results */
                    <div>
                      <div className="admin-search-wrapper w-full">
                        <Search size={16} />
                        <input
                          type="text"
                          className="admin-search-input"
                          style={{ height: '42px', fontSize: '13px' }}
                          placeholder="ابحث برقم الهاتف أو اسم الطالب أو جامعته..."
                          value={studentSearch}
                          onChange={(e) => setStudentSearch(e.target.value)}
                        />
                        {studentSearch && (
                          <button
                            type="button"
                            onClick={() => setStudentSearch('')}
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#94a3b8',
                            }}
                          >
                            <X size={15} />
                          </button>
                        )}
                      </div>

                      {/* Instant Match Dropdown (Name, Phone, University ONLY) */}
                      {searchedStudents.length > 0 && (
                        <div
                          style={{
                            marginTop: '8px',
                            maxHeight: '190px',
                            overflowY: 'auto',
                            backgroundColor: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                          }}
                        >
                          {searchedStudents.map((s) => (
                            <div
                              key={s.uid}
                              onClick={() => {
                                setSelectedStudent(s);
                                setStudentSearch('');
                                if (s.universityId) setSelectedUniId(s.universityId);
                              }}
                              style={{
                                padding: '10px 14px',
                                borderBottom: '1px solid #f1f5f9',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                transition: 'background-color 0.15s',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                            >
                              <div>
                                {/* 1. Name */}
                                <strong style={{ fontSize: '13px', color: '#0f172a', display: 'block' }}>
                                  {s.fullName}
                                </strong>
                                {/* 2. Phone */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#475569', marginTop: '2px' }} dir="ltr">
                                  <Phone size={11} className="text-[#E5232A]" />
                                  <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{s.phoneNumber || '—'}</span>
                                </div>
                              </div>

                              {/* 3. University ONLY */}
                              <span
                                style={{
                                  fontSize: '11px',
                                  backgroundColor: '#f1f5f9',
                                  color: '#334155',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  maxWidth: '140px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {s.universityName || 'طالب مسجل'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Booking Summary Box (Handles Queue or Single Trip) */}
                <div
                  style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '18px',
                    border: bookingQueue.length > 0 ? '1.5px solid #cbd5e1' : '1px solid #e2e8f0',
                    padding: '16px',
                  }}
                >
                  {bookingQueue.length > 0 ? (
                    /* MULTI-BOOKING QUEUE VIEW */
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Layers size={16} className="text-[#E5232A]" />
                          <strong style={{ fontSize: '13px', color: '#0f172a' }}>
                            قائمة الرحلات المحددة ({bookingQueue.length} رحلة)
                          </strong>
                        </div>
                        <button
                          type="button"
                          onClick={handleClearQueue}
                          style={{
                            padding: '2px 8px',
                            borderRadius: '6px',
                            border: '1px solid #fecaca',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                        >
                          تفريغ الكل
                        </button>
                      </div>

                      <div
                        style={{
                          maxHeight: '230px',
                          overflowY: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          paddingRight: '2px',
                        }}
                      >
                        {bookingQueue.map((item) => (
                          <div
                            key={item.id}
                            style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '10px',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                              <span style={{ fontSize: '14px' }}>
                                {item.direction === 'OUTBOUND' ? '☀️' : '🌙'}
                              </span>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                  <strong style={{ color: '#0f172a', fontFamily: 'monospace' }}>{item.date}</strong>
                                  <span style={{ color: '#64748b' }}>({getDayOfWeekName(item.date)})</span>
                                  <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#E5232A' }}>
                                    {item.departureTime}
                                  </span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item.universityName}
                                  </span>
                                  <span style={{ fontWeight: 'bold', color: '#16a34a' }}>مقعد #{item.seatNumber}</span>
                                  <span style={{ fontWeight: 'bold', color: '#0f172a', fontFamily: 'monospace' }}>{item.price} ج.م</span>
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveFromQueue(item.id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc2626',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title="حذف هذه الرحلة من القائمة"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>

                      <div
                        style={{
                          marginTop: '12px',
                          paddingTop: '10px',
                          borderTop: '1px solid #e2e8f0',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>
                          إجمالي ({bookingQueue.length} رحلة):
                        </span>
                        <span
                          style={{
                            fontSize: '18px',
                            fontWeight: '800',
                            color: '#E5232A',
                            fontFamily: 'monospace',
                          }}
                        >
                          {bookingQueue.reduce((acc, curr) => acc + (curr.price || 0), 0)} ج.م
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* SINGLE/ROUND-TRIP SUMMARY VIEW */
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b' }}>
                          ملخص بيانات الحجز:
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: tripBookingMode === 'ROUND_TRIP' ? '#E5232A' : '#334155',
                            backgroundColor: tripBookingMode === 'ROUND_TRIP' ? '#FFF1F2' : '#f1f5f9',
                            padding: '2px 8px',
                            borderRadius: '6px',
                          }}
                        >
                          {tripBookingMode === 'ROUND_TRIP' ? 'ذهاب وعودة (نفس اليوم)' : tripBookingMode === 'ONE_WAY_OUTBOUND' ? 'ذهاب فقط' : 'عودة فقط'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                        {/* Common Date */}
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#64748b' }}>تاريخ السفر:</span>
                          <strong style={{ color: '#0f172a', fontFamily: 'monospace' }}>{selectedDate}</strong>
                        </div>

                        {/* If Round Trip: Show Both Outbound and Return */}
                        {tripBookingMode === 'ROUND_TRIP' ? (
                          <>
                            {/* Outbound item */}
                            <div style={{ backgroundColor: '#ffffff', borderRadius: '10px', padding: '8px 10px', border: '1px solid #f1f5f9' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <span style={{ fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span>☀️</span>
                                  <span>رحلة الذهاب:</span>
                                </span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#059669' }}>
                                  {selectedTrip ? `${selectedTrip.price} ج.م` : '—'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', fontSize: '11px' }}>
                                <span>{selectedTrip ? `${selectedTrip.universityName} (${selectedTrip.departureTime})` : 'لم تحدد بعد'}</span>
                                <strong style={{ color: selectedSeatNumber ? '#15803d' : '#94a3b8' }}>
                                  {selectedSeatNumber ? `مقعد #${selectedSeatNumber}` : 'بدون مقعد'}
                                </strong>
                              </div>
                            </div>

                            {/* Return item */}
                            <div style={{ backgroundColor: '#ffffff', borderRadius: '10px', padding: '8px 10px', border: '1px solid #f1f5f9' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <span style={{ fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span>🌙</span>
                                  <span>رحلة العودة:</span>
                                </span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#059669' }}>
                                  {selectedReturnTrip ? `${selectedReturnTrip.price} ج.م` : '—'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', fontSize: '11px' }}>
                                <span>{selectedReturnTrip ? `${selectedReturnTrip.universityName} (${selectedReturnTrip.departureTime})` : 'لم تحدد بعد'}</span>
                                <strong style={{ color: selectedReturnSeatNumber ? '#15803d' : '#94a3b8' }}>
                                  {selectedReturnSeatNumber ? `مقعد #${selectedReturnSeatNumber}` : 'بدون مقعد'}
                                </strong>
                              </div>
                            </div>

                            {/* Total Price for Round Trip */}
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '8px',
                                marginTop: '2px',
                                borderTop: '1px solid #e2e8f0',
                              }}
                            >
                              <span style={{ fontWeight: 'bold', color: '#0f172a' }}>إجمالي التذكرتين (ذهاب وعودة):</span>
                              <span
                                style={{
                                  fontSize: '18px',
                                  fontWeight: '800',
                                  color: '#E5232A',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {(selectedTrip?.price || 0) + (selectedReturnTrip?.price || 0)} ج.م
                              </span>
                            </div>
                          </>
                        ) : (
                          /* Single Trip Mode Summary */
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#64748b' }}>الرحلة المختارة:</span>
                              <strong style={{ color: selectedTrip ? '#0f172a' : '#94a3b8' }}>
                                {selectedTrip ? `${selectedTrip.universityName} (${selectedTrip.departureTime})` : 'لم تحدد بعد'}
                              </strong>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#64748b' }}>رقم المقعد:</span>
                              <strong style={{ color: selectedSeatNumber ? '#15803d' : '#94a3b8' }}>
                                {selectedSeatNumber ? `#${selectedSeatNumber}` : 'لم يختر بعد'}
                              </strong>
                            </div>

                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '8px',
                                marginTop: '2px',
                                borderTop: '1px solid #e2e8f0',
                              }}
                            >
                              <span style={{ fontWeight: 'bold', color: '#0f172a' }}>سعر التذكرة الإجمالي:</span>
                              <span
                                style={{
                                  fontSize: '17px',
                                  fontWeight: '800',
                                  color: '#E5232A',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {selectedTrip?.price || 0} ج.م
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* STEP 4: Payment Method & Status */}
                <div style={{ marginTop: 'auto' }}>
                  <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                    <CreditCard size={17} className="text-[#E5232A]" />
                    <span>الخطوة 4: وسيلة السداد والتأكيد</span>
                  </label>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                        وسيلة الدفع:
                      </span>
                      <select
                        className="admin-filter-select w-full"
                        style={{ height: '40px', fontSize: '13px', padding: '0 10px', boxSizing: 'border-box' }}
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
                      >
                        <option value="CASH">دفع نقدي (كاش بالمقر / عند الركوب)</option>
                        <option value="INSTAPAY">تحويل إنستاباي (InstaPay)</option>
                        <option value="WALLET">محفظة إلكترونية (فودافون كاش...)</option>
                        <option value="BRANCH">سداد في الفرع</option>
                      </select>
                    </div>

                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                        حالة التحصيل:
                      </span>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '8px',
                          padding: '2px',
                          gap: '2px',
                          height: '40px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setIsPaid(true)}
                          style={{
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            backgroundColor: isPaid ? '#16a34a' : 'transparent',
                            color: isPaid ? '#ffffff' : '#64748b',
                          }}
                        >
                          تم التحصيل
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsPaid(false)}
                          style={{
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            backgroundColor: !isPaid ? '#d97706' : 'transparent',
                            color: !isPaid ? '#ffffff' : '#64748b',
                          }}
                        >
                          قيد السداد
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Admin Note */}
                  <div style={{ marginBottom: '14px' }}>
                    <input
                      type="text"
                      className="admin-search-input w-full"
                      style={{ height: '38px', fontSize: '12px', paddingRight: '10px' }}
                      placeholder="ملاحظة إدارية على الحجز (اختياري)..."
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      type="button"
                      className="btn-admin-outline"
                      style={{ flex: 1, height: '44px' }}
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      إلغاء
                    </button>

                    <button
                      type="submit"
                      className="btn-admin-primary"
                      style={{
                        flex: 2,
                        height: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px',
                        fontWeight: 'bold',
                      }}
                      disabled={
                        isSubmitting ||
                        !selectedStudent ||
                        (bookingQueue.length > 0
                          ? false
                          : tripBookingMode === 'ROUND_TRIP'
                          ? !selectedTrip || !selectedSeatNumber || !selectedReturnTrip || !selectedReturnSeatNumber
                          : !selectedTrip || !selectedSeatNumber)
                      }
                    >
                      <CheckCircle2 size={18} />
                      <span>
                        {isSubmitting
                          ? (editingAttempt ? 'جاري حفظ وتحديث التعديل...' : 'جاري تأكيد وتوليد الحجوزات...')
                          : editingAttempt
                          ? (bookingQueue.length > 0
                              ? `حفظ وتحديث حجز الرحلات (${bookingQueue.length} رحلة)`
                              : tripBookingMode === 'ROUND_TRIP'
                              ? 'حفظ وتحديث حجز الذهاب والعودة'
                              : 'حفظ وتحديث حجز الرحلة')
                          : bookingQueue.length > 0
                          ? `تأكيد حجز كافة الرحلات (${bookingQueue.length} رحلة) وتوليد التذاكر`
                          : tripBookingMode === 'ROUND_TRIP'
                          ? 'تأكيد حجز الذهاب والعودة وتوليد التذاكر'
                          : 'تأكيد الحجز وتوليد التذكرة'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* LEFT COLUMN: Calendar & Trip Selection & Bus Seat Layout */}
              <div
                style={{
                  order: 2,
                  padding: '20px 24px',
                  borderRight: '1px solid #e2e8f0',
                  overflowY: 'auto',
                  backgroundColor: '#f8fafc',
                }}
              >
                {/* STEP 2: University Line & Journey Mode Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={18} className="text-[#E5232A]" />
                      <span>الخطوة 2: اختيار الجامعة ونوع الرحلة واليوم عبر الكلندر</span>
                    </label>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.05fr 1.65fr', gap: '10px', marginBottom: '12px' }}>
                    {/* University Line Selector */}
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                        خط الجامعة:
                      </span>
                      <select
                        className="admin-filter-select w-full"
                        style={{ height: '40px', fontSize: '13px', padding: '0 12px', boxSizing: 'border-box' }}
                        value={selectedUniId}
                        onChange={(e) => {
                          setSelectedUniId(e.target.value);
                          setSelectedTrip(null);
                          setSelectedReturnTrip(null);
                        }}
                      >
                        <option value="ALL">كافة الجامعات ({universities.length})</option>
                        {universities.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Journey Mode Selector (Round-Trip vs Outbound vs Return) */}
                    <div>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', display: 'block', marginBottom: '4px' }}>
                        نوع الرحلة والاتجاه:
                      </span>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1.25fr 1fr 1fr',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '10px',
                          padding: '3px',
                          gap: '3px',
                          height: '40px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setTripBookingMode('ROUND_TRIP');
                            setSelectedTrip(null);
                            setSelectedReturnTrip(null);
                          }}
                          style={{
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            backgroundColor: tripBookingMode === 'ROUND_TRIP' ? '#ffffff' : 'transparent',
                            color: tripBookingMode === 'ROUND_TRIP' ? '#E5232A' : '#64748b',
                            boxShadow: tripBookingMode === 'ROUND_TRIP' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '3px',
                          }}
                        >
                          <RefreshCw size={12} />
                          <span>ذهاب وعودة</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setTripBookingMode('ONE_WAY_OUTBOUND');
                            setSelectedTrip(null);
                            setSelectedReturnTrip(null);
                          }}
                          style={{
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            backgroundColor: tripBookingMode === 'ONE_WAY_OUTBOUND' ? '#ffffff' : 'transparent',
                            color: tripBookingMode === 'ONE_WAY_OUTBOUND' ? '#E5232A' : '#64748b',
                            boxShadow: tripBookingMode === 'ONE_WAY_OUTBOUND' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '3px',
                          }}
                        >
                          <span>☀️</span>
                          <span>ذهاب فقط</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setTripBookingMode('ONE_WAY_RETURN');
                            setSelectedTrip(null);
                            setSelectedReturnTrip(null);
                          }}
                          style={{
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            backgroundColor: tripBookingMode === 'ONE_WAY_RETURN' ? '#ffffff' : 'transparent',
                            color: tripBookingMode === 'ONE_WAY_RETURN' ? '#E5232A' : '#64748b',
                            boxShadow: tripBookingMode === 'ONE_WAY_RETURN' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '3px',
                          }}
                        >
                          <span>🌙</span>
                          <span>عودة فقط</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* INTERACTIVE MONTHLY CALENDAR GRID */}
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '18px',
                      border: '1px solid #e2e8f0',
                      padding: '14px',
                      boxShadow: '0 2px 10px rgba(15, 23, 42, 0.03)',
                    }}
                  >
                    {/* Mode Switcher & Presets Header */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setIsMultiMode(false);
                            setSelectedDates([selectedDate]);
                          }}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            border: !isMultiMode ? '1.5px solid #E5232A' : '1px solid #cbd5e1',
                            backgroundColor: !isMultiMode ? '#FFF1F2' : '#ffffff',
                            color: !isMultiMode ? '#E5232A' : '#475569',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                        >
                          حجز يوم محدد
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsMultiMode(true);
                            if (!selectedDates.includes(selectedDate)) {
                              setSelectedDates([selectedDate]);
                            }
                          }}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            border: isMultiMode ? '1.5px solid #E5232A' : '1px solid #cbd5e1',
                            backgroundColor: isMultiMode ? '#FFF1F2' : '#ffffff',
                            color: isMultiMode ? '#E5232A' : '#475569',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <CheckSquare size={13} />
                          <span>تحديد أيام ومواعيد متعددة ({selectedDates.length})</span>
                        </button>
                      </div>

                      {isMultiMode && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>نماذج سريعة:</span>
                          <button
                            type="button"
                            onClick={() => applyWeekdayPreset('MW')}
                            style={{
                              padding: '3px 8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              backgroundColor: '#ffffff',
                              color: '#334155',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                            title="تحديد كل سبت / اثنين / أربعاء"
                          >
                            سبت/اثنين/أربعاء
                          </button>
                          <button
                            type="button"
                            onClick={() => applyWeekdayPreset('TT')}
                            style={{
                              padding: '3px 8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              backgroundColor: '#ffffff',
                              color: '#334155',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                            title="تحديد كل أحد / ثلاثاء / خميس"
                          >
                            أحد/ثلاثاء/خميس
                          </button>
                          <button
                            type="button"
                            onClick={() => applyWeekdayPreset('ALL_WEEK')}
                            style={{
                              padding: '3px 8px',
                              borderRadius: '6px',
                              border: '1px solid #cbd5e1',
                              backgroundColor: '#ffffff',
                              color: '#334155',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                            title="أسبوع دراسي كامل"
                          >
                            أسبوع دراسي
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedDates([selectedDate])}
                            style={{
                              padding: '3px 8px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              fontSize: '11px',
                              cursor: 'pointer',
                            }}
                          >
                            تفريغ
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Selected Dates Chips (if Multi-Mode) */}
                    {isMultiMode && selectedDates.length > 0 && (
                      <div
                        style={{
                          marginBottom: '10px',
                          padding: '8px 10px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>
                          الأيام المحددة ({selectedDates.length}):
                        </span>
                        {selectedDates.slice().sort().map((d) => (
                          <span
                            key={d}
                            onClick={() => setSelectedDate(d)}
                            style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              backgroundColor: selectedDate === d ? '#E5232A' : '#ffffff',
                              color: selectedDate === d ? '#ffffff' : '#0f172a',
                              border: selectedDate === d ? '1px solid #E5232A' : '1px solid #cbd5e1',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>{d} ({getDayOfWeekName(d)})</span>
                            {selectedDates.length > 1 && (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedDates((prev) => prev.filter((item) => item !== d));
                                }}
                                style={{ fontWeight: 'bold', cursor: 'pointer', opacity: 0.8 }}
                              >
                                ✕
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Month Header and Navigation Controls */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f1f5f9',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={handleToday}
                          style={{
                            padding: '4px 10px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#f8fafc',
                            color: '#334155',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                          }}
                        >
                          اليوم
                        </button>
                        <strong style={{ fontSize: '15px', color: '#0f172a' }}>
                          {formatMonthYearHeader(calendarMonth)}
                        </strong>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                          type="button"
                          onClick={handlePrevMonth}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#475569',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          title="الشهر السابق"
                        >
                          <ChevronRight size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={handleNextMonth}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            color: '#475569',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          title="الشهر التالي"
                        >
                          <ChevronLeft size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Weekday Names Row */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '6px',
                        textAlign: 'center',
                      }}
                    >
                      {ARABIC_WEEKDAYS.map((dayName, idx) => (
                        <span key={idx} style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>
                          {dayName}
                        </span>
                      ))}
                    </div>

                    {/* Days Grid */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '5px',
                      }}
                    >
                      {calendarDays.map((day, idx) => {
                        const isSelected = isMultiMode
                          ? selectedDates.includes(day.dateStr)
                          : selectedDate === day.dateStr;
                        const isCurrentMonth = day.isCurrentMonth;
                        const hasTrips = day.hasTrips;

                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              if (isMultiMode) {
                                if (selectedDates.includes(day.dateStr)) {
                                  if (selectedDates.length > 1) {
                                    setSelectedDates((prev) => prev.filter((d) => d !== day.dateStr));
                                  }
                                } else {
                                  setSelectedDates((prev) => [...prev, day.dateStr]);
                                }
                              } else {
                                setSelectedDates([day.dateStr]);
                              }
                              setSelectedDate(day.dateStr);
                              setSelectedTrip(null);
                              setSelectedReturnTrip(null);
                            }}
                            style={{
                              aspectRatio: '1 / 0.88',
                              borderRadius: '10px',
                              padding: '4px 5px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              border: isSelected
                                ? '2px solid #E5232A'
                                : day.isToday
                                ? '1.5px solid #cbd5e1'
                                : '1px solid #f1f5f9',
                              backgroundColor: isSelected
                                ? '#FFF1F2'
                                : hasTrips
                                ? '#f0fdf4'
                                : !isCurrentMonth
                                ? '#f8fafc'
                                : '#ffffff',
                              opacity: !isCurrentMonth ? 0.35 : 1,
                              boxShadow: isSelected ? '0 2px 8px rgba(229, 35, 42, 0.15)' : 'none',
                              position: 'relative',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span
                                style={{
                                  fontSize: '12px',
                                  fontWeight: isSelected || day.isToday ? '800' : '600',
                                  color: isSelected ? '#E5232A' : '#1e293b',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {day.dayNumber}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                {isMultiMode && isSelected && (
                                  <span style={{ fontSize: '10px', color: '#16a34a', fontWeight: 'bold' }}>✓</span>
                                )}
                                {day.isToday && (
                                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#E5232A' }} />
                                )}
                              </div>
                            </div>

                            {/* Trip availability badge */}
                            {hasTrips ? (
                              <div
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 'bold',
                                  color: isSelected ? '#E5232A' : '#15803d',
                                  backgroundColor: isSelected ? '#ffffff' : '#dcfce7',
                                  padding: '1px 3px',
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                }}
                              >
                                {tripBookingMode === 'ROUND_TRIP' && day.hasBothWays
                                  ? 'ذهاب وعودة ✓'
                                  : `${day.tripsCount} رحلة`}
                              </div>
                            ) : (
                              <div style={{ height: '12px' }} />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Calendar Legend Bar */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '10px',
                        paddingTop: '8px',
                        borderTop: '1px solid #f1f5f9',
                        fontSize: '11px',
                        color: '#64748b',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#dcfce7', border: '1px solid #86efac' }} />
                          <span>يوجد رحلات</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#FFF1F2', border: '1.5px solid #E5232A' }} />
                          <span>
                            {isMultiMode ? `أيام محددة (${selectedDates.length})` : `اليوم المحدد (${selectedDate})`}
                          </span>
                        </span>
                      </div>

                      <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#0f172a' }}>
                        {isMultiMode
                          ? `معاينة رحلات: ${selectedDate}`
                          : `تاريخ الحجز: ${selectedDate}`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* TRIPS SELECTION & BUS SEATS */}
                {tripBookingMode === 'ROUND_TRIP' ? (
                  /* =================================================== */
                  /* ROUND TRIP MODE: OUTBOUND SECTION + RETURN SECTION   */
                  /* =================================================== */
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {/* 1. OUTBOUND SECTION ☀️ */}
                    <div style={{ backgroundColor: '#ffffff', borderRadius: '18px', border: '1px solid #e2e8f0', padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '16px' }}>☀️</span>
                          <span>1. رحلة الذهاب للجامعة (صباحاً) — {selectedDate}</span>
                        </span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {availableOutboundTrips.length} رحلة ذهاب متاحة
                        </span>
                      </div>

                      {availableOutboundTrips.length === 0 ? (
                        <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
                          <span>لا توجد رحلات ذهاب مجدولة في هذا التاريخ المحدد ({selectedDate}).</span>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px' }}>
                          {availableOutboundTrips.map((t) =>
                            renderTripCard(t, selectedTrip?.id === t.id, () => setSelectedTrip(t))
                          )}
                        </div>
                      )}

                      {/* Outbound Seat Map */}
                      {selectedTrip && renderSeatMap(
                        selectedTrip,
                        selectedSeatNumber,
                        (s) => setSelectedSeatNumber(s),
                        occupiedSeats,
                        tripManifestLoading,
                        'مقعد رحلة الذهاب'
                      )}
                    </div>

                    {/* 2. RETURN SECTION 🌙 */}
                    <div style={{ backgroundColor: '#ffffff', borderRadius: '18px', border: '1px solid #e2e8f0', padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '16px' }}>🌙</span>
                          <span>2. رحلة العودة للإسكندرية (مساءً) — {selectedDate}</span>
                        </span>
                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                          {availableReturnTrips.length} رحلة عودة متاحة
                        </span>
                      </div>

                      {availableReturnTrips.length === 0 ? (
                        <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <AlertCircle size={16} className="text-amber-600 flex-shrink-0" />
                          <span>لا توجد رحلات عودة مجدولة في هذا التاريخ المحدد ({selectedDate}).</span>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px' }}>
                          {availableReturnTrips.map((t) =>
                            renderTripCard(t, selectedReturnTrip?.id === t.id, () => setSelectedReturnTrip(t))
                          )}
                        </div>
                      )}

                      {/* Return Seat Map */}
                      {selectedReturnTrip && renderSeatMap(
                        selectedReturnTrip,
                        selectedReturnSeatNumber,
                        (s) => setSelectedReturnSeatNumber(s),
                        returnOccupiedSeats,
                        returnTripManifestLoading,
                        'مقعد رحلة العودة'
                      )}
                    </div>

                    {/* Round-Trip Batch Actions Bar */}
                    {(selectedTrip || selectedReturnTrip) && (
                      <div
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          border: '1.5px dashed #E5232A',
                          padding: '14px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '12px',
                        }}
                      >
                        <div>
                          <strong style={{ fontSize: '13px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Layers size={16} className="text-[#E5232A]" />
                            <span>
                              إضافة الذهاب {selectedTrip ? `(${selectedTrip.departureTime})` : ''} والعودة {selectedReturnTrip ? `(${selectedReturnTrip.departureTime})` : ''} لقائمة الحجز
                            </span>
                          </strong>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            احجز هذا اليوم أو كرره لكافة الأيام المحددة دفعة واحدة
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleAddRoundTripToQueue(false)}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '8px',
                              backgroundColor: '#FFF1F2',
                              border: '1px solid #FECDD3',
                              color: '#E5232A',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <Plus size={14} />
                            <span>إضافة الذهاب والعودة للقائمة</span>
                          </button>

                          {isMultiMode && selectedDates.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleAddRoundTripToQueue(true)}
                              style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                backgroundColor: '#E5232A',
                                border: '1px solid #E5232A',
                                color: '#ffffff',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <RefreshCw size={13} />
                              <span>تكرار لكافة الأيام المحددة ({selectedDates.length})</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* =================================================== */
                  /* SINGLE TRIP MODE (OUTBOUND OR RETURN ONLY)          */
                  /* =================================================== */
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#0f172a' }}>
                        الرحلات المتاحة في تاريخ ({selectedDate}):
                      </span>
                      <span style={{ fontSize: '11px', color: '#64748b' }}>
                        {tripBookingMode === 'ONE_WAY_OUTBOUND' ? availableOutboundTrips.length : availableReturnTrips.length} رحلة مطابقة
                      </span>
                    </div>

                    {(tripBookingMode === 'ONE_WAY_OUTBOUND' ? availableOutboundTrips : availableReturnTrips).length === 0 ? (
                      <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <AlertCircle size={18} className="text-amber-600 flex-shrink-0" />
                        <div>
                          <strong>لا توجد رحلات مجدولة في هذا التاريخ المحدد ({selectedDate}).</strong>
                          <div style={{ marginTop: '2px', color: '#b45309' }}>
                            اضغط على أي يوم آخر يظهر باللون الأخضر في الكلندر أعلاه لاختيار رحلة متاحة.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                        {(tripBookingMode === 'ONE_WAY_OUTBOUND' ? availableOutboundTrips : availableReturnTrips).map((t) =>
                          renderTripCard(t, selectedTrip?.id === t.id, () => setSelectedTrip(t))
                        )}
                      </div>
                    )}

                    {/* Single Trip Seat Map */}
                    {selectedTrip && renderSeatMap(
                      selectedTrip,
                      selectedSeatNumber,
                      (s) => setSelectedSeatNumber(s),
                      occupiedSeats,
                      tripManifestLoading,
                      'اختيار المقعد بالحافلة'
                    )}

                    {/* Single Trip Batch Actions Bar */}
                    {selectedTrip && (
                      <div
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: '16px',
                          border: '1.5px dashed #E5232A',
                          padding: '14px 18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '12px',
                          marginTop: '12px',
                        }}
                      >
                        <div>
                          <strong style={{ fontSize: '13px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Layers size={16} className="text-[#E5232A]" />
                            <span>
                              الرحلة المحددة: {selectedTrip.departureTime} ({selectedTrip.universityName})
                            </span>
                          </strong>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>
                            المقعد: {selectedSeatNumber ? `#${selectedSeatNumber}` : 'تلقائي'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleAddTripToQueue(selectedTrip)}
                            style={{
                              padding: '8px 14px',
                              borderRadius: '8px',
                              backgroundColor: '#FFF1F2',
                              border: '1px solid #FECDD3',
                              color: '#E5232A',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <Plus size={14} />
                            <span>إضافة للقائمة</span>
                          </button>

                          {isMultiMode && selectedDates.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleAddTripToAllSelectedDates(selectedTrip)}
                              style={{
                                padding: '8px 14px',
                                borderRadius: '8px',
                                backgroundColor: '#E5232A',
                                border: '1px solid #E5232A',
                                color: '#ffffff',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                              }}
                            >
                              <RefreshCw size={13} />
                              <span>تكرار لكافة الأيام المحددة ({selectedDates.length})</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        )}

        {/* FULL-SCREEN STUDENT DIRECTORY OVERLAY (Name, Phone, University ONLY) */}
        {showStudentDirectory && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: '#ffffff',
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '24px',
              overflow: 'hidden',
            }}
          >
            {/* Directory Header */}
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#ffffff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    backgroundColor: '#FFF1F2',
                    color: '#E5232A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Users size={20} />
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
                    سجل الطلاب المسجلين بالمنظومة (اختر الطالب)
                  </h3>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    يعرض الاسم ورقم الهاتف والجامعة فقط لتسهيل وسرعة الاختيار
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowStudentDirectory(false)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc',
                  color: '#334155',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                إغلاق القائمة ✕
              </button>
            </div>

            {/* Directory Search Bar */}
            <div style={{ padding: '14px 24px', borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' }}>
              <div className="admin-search-wrapper w-full">
                <Search size={18} />
                <input
                  type="text"
                  className="admin-search-input"
                  style={{ height: '44px', fontSize: '13px' }}
                  placeholder="ابحث باسم الطالب، رقم هاتفه، أو جامعته لتصفية السجل فوراً..."
                  value={directorySearch}
                  onChange={(e) => setDirectorySearch(e.target.value)}
                  autoFocus
                />
                {directorySearch && (
                  <button
                    type="button"
                    onClick={() => setDirectorySearch('')}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#94a3b8',
                    }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Directory Student Cards Grid (Name, Phone, University ONLY) */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px 24px',
                backgroundColor: '#f8fafc',
              }}
            >
              {directoryFilteredStudents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
                  <Users size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ fontSize: '14px', fontWeight: 'bold' }}>لا يوجد طلاب مطابقون لبحثك</p>
                  <span style={{ fontSize: '12px' }}>تأكد من كتابة الاسم أو رقم الهاتف أو الجامعة بشكل صحيح</span>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '12px',
                  }}
                >
                  {directoryFilteredStudents.map((s) => (
                    <div
                      key={s.uid}
                      onClick={() => {
                        setSelectedStudent(s);
                        setShowStudentDirectory(false);
                        if (s.universityId) setSelectedUniId(s.universityId);
                      }}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '16px',
                        border: '1.5px solid #e2e8f0',
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#E5232A';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(229, 35, 42, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.03)';
                      }}
                    >
                      {/* 1. Name Only */}
                      <strong style={{ fontSize: '14px', color: '#0f172a', marginBottom: '8px', lineHeight: 1.4 }}>
                        {s.fullName}
                      </strong>

                      {/* 2. Phone Number & 3. University Only */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#334155' }}>
                          <Phone size={13} className="text-[#E5232A] flex-shrink-0" />
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }} dir="ltr">
                            {s.phoneNumber || '—'}
                          </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b' }}>
                          <GraduationCap size={14} className="text-slate-400 flex-shrink-0" />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {s.universityName || 'جامعة غير محددة'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
